import {createContext, use} from 'react'

import {CellValidationContextProvider} from '../../../components/table/cell-validation'
import {
  RowMenuShortcutContext,
  RowMenuShortcutProvider,
  type RowMenuShortcutState,
} from '../../../components/table/state-providers/row-menu-shortcut-provider'
import type {MemexItemModel} from '../../../models/memex-item-model'
import type {Table, TableState} from '../helpers/types'
import {ColumnOrderDndProvider} from './columns/column-order-dnd-provider'

export type TableStateSelectedRowIdsContextValue = TableState['rowSelection']
type TableStateColumnResizingContextValue = TableState['columnSizing']
export type TableContextValue = Table<MemexItemModel>

export const TableStateSelectedRowIdsContext = createContext<TableStateSelectedRowIdsContextValue | null>(null)
const TableStateColumnResizingContext = createContext<TableStateColumnResizingContextValue | null>(null)
export const TableContext = createContext<TableContextValue | null>(null)

/**
 * Sets up a context in which consumers can access the React Table instance
 *
 * @param props Props with a `table` attribute
 */
export const TableProvider: React.FC<{
  table: Table<MemexItemModel>
  children: React.ReactNode
}> = ({table, children}) => {
  const state = table.getState()
  return (
    <ColumnOrderDndProvider>
      <TableStateSelectedRowIdsContext value={state.rowSelection}>
        <RowMenuShortcutProvider>
          <TableStateColumnResizingContext value={state.columnSizing}>
            <TableContext value={table}>
              <CellValidationContextProvider>{children}</CellValidationContextProvider>
            </TableContext>
          </TableStateColumnResizingContext>
        </RowMenuShortcutProvider>
      </TableStateSelectedRowIdsContext>
    </ColumnOrderDndProvider>
  )
}

/**
 * Get a reference to the table instance object.
 *
 * @link [Table API docs](https://tanstack.com/table/latest/docs/api/core/table)
 *
 * Caution: Be careful with this hook—TanStack `useTable` returns a new table
 * object when the state it subscribes to changes, so consumers of this hook
 * will re-render on those table state updates. Do not rely on the table
 * instance being referentially stable.
 */
export function useTableInstance(): Table<MemexItemModel> {
  const table = use(TableContext)

  if (!table) {
    throw new Error('Must use `useTableInstance` in child of `<TableProvider>`')
  }

  return table
}

/**
 * Get the table's row menu shortcut origin.
 */
export function useTableRowMenuShortcutOrigin(): RowMenuShortcutState['rowMenuShortcutOrigin'] {
  const rowMenuContext = use(RowMenuShortcutContext)
  return rowMenuContext?.rowMenuState?.rowMenuShortcutOrigin
}

/**
 * Get the table's internal column resizing state.
 */
export function useTableColumnResizing(): TableStateColumnResizingContextValue {
  const columnResizing = use(TableStateColumnResizingContext)

  if (!columnResizing) {
    throw new Error('Must use `useTableColumnResizing` in child of `<TableProvider>')
  }

  return columnResizing
}
